import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { dequal } from "dequal";
import type { AppDispatch, RootState } from "@/store/store";
import { parseURLParams } from "@/utils/url";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import {
  isLegacyURL,
  convertOldURLToNewURL,
} from "@/utils2/convertOldURLToNewURL";

/**
 * Fires exactly once after bootstrap data (brands + markets) is ready.
 *
 * If the current URL is a legacy (pre-redesign) pathname (e.g. /segments/equity,
 * /competitive/trends, /advanced/butterfly …) it converts the URL to the new
 * format via `convertOldURLToNewURL` and navigates there — mirroring the
 * `getNewURL` branch that the old `useURLSync` hook handled.  The
 * `hasBootstrapped` guard is intentionally NOT set in this branch so that the
 * effect re-fires after navigation and processes the converted URL normally.
 *
 * For non-legacy URLs it reads the current URL search params, derives the
 * module from the first pathname segment, and dispatches
 * `setPendingSelectionIfChanged` with that payload.
 *
 * From there the full Pending Selection → RTK Listener → merge + validate →
 * update URL → useURLToStore → syncFromURL → userSelection flow takes over
 * automatically.
 *
 * Must be rendered inside the Router tree (requires `useLocation`).
 */
export const useInitialURLBootstrap = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const hasBootstrapped = useRef(false);

  const isDataFetched = useSelector(
    (state: RootState) => state.activeGroup.bootstrapDataStatus.isDataFetched,
  );

  // Markets are needed to translate legacy market identity strings → numeric IDs
  // when converting old URLs.  They are available by the time isDataFetched is true.
  const markets = useSelector(
    (state: RootState) => state.activeGroup.markets.markets,
    dequal,
  );

  useEffect(() => {
    if (!isDataFetched || hasBootstrapped.current) return;

    // ── Legacy URL handling ──────────────────────────────────────────────────
    // If the user navigated to (or bookmarked) a pre-redesign URL such as
    // /segments/equity or /advanced/butterfly, convert it to the equivalent
    // new-format URL and navigate there.
    //
    // We intentionally do NOT set hasBootstrapped.current = true here so that
    // the effect re-fires after the navigation and the converted URL is
    // processed through the normal bootstrap path below.
    if (isLegacyURL(location.pathname)) {
      const { pathname, url } = convertOldURLToNewURL(location.search, markets, location.pathname);
      navigate({ pathname, search: url }, { replace: true });
      return;
    }

    // Derive module from the first pathname segment (e.g. "/equity/brand" → "equity")
    // and screenType from the second segment (e.g. "brand", "segment", "market").
    // Both must be included in the pending dispatch so the selectionListener
    // builds the correct target pathname (e.g. "/conversion/brand") rather than
    // falling back to a stale userSelection.screenType that may have been set by
    // useURLToStore when it processed the legacy URL before the conversion ran.
    const pathSegments = location.pathname.split("/");
    const module = pathSegments?.[1] ?? "";
    const screenType = pathSegments?.[2] ?? "brand";

    // Wait until Home.tsx has redirected away from "/" before bootstrapping.
    // If we fire while still on "/" the module will be "" and the
    // selectionListener will find no config → early-return without navigating.
    if (!module) return;

    // Guard: fire exactly once (after the pathname is a real module path)
    hasBootstrapped.current = true;

    const searchParams = new URLSearchParams(location.search);
    const parsed = parseURLParams(searchParams);

    // `parsed.filters`, if present, is now a structured `FilterSelection[]`
    // produced by the v2 `fv=2` + `f=` URL grammar (spec §6.5.1) — pass it
    // through to `setPendingSelectionIfChanged` unmodified via the `...parsed`
    // spread below. A legacy numeric-id `filters` param on the initial-load
    // URL (no `fv=2`) produces no `filters` key at all (see `parseURLParams`),
    // so there is nothing here to strip.

    dispatch(
      setPendingSelectionIfChanged({
        ...parsed,
        module,
        screenType,
      } as any),
    );
  }, [isDataFetched, location.pathname]);
};

export default useInitialURLBootstrap;
